<template>
  <div class="followupbot-messages">
    <v-container fluid class="px-6 py-4">
      <!-- Header Section -->
      <div class="header-section mb-6">
        <div class="header-content">
          <h1 class="page-title">
            <v-icon large class="mr-3" color="#667eea">mdi-email-multiple</v-icon>
            Generated Messages
          </h1>
          <v-btn
            color="success"
            large
            elevation="2"
            @click="fetchDueForSending"
            class="action-btn"
          >
            <v-icon left>mdi-clock-alert</v-icon>
            Due for Sending
          </v-btn>
        </div>
      </div>

      <!-- Filters Section -->
      <v-card class="filters-card mb-6" elevation="4">
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.search"
                label="Search messages..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                @input="applyFilters"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.status"
                label="Status"
                :items="statusOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.tone"
                label="Message Tone"
                :items="toneOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.date_range"
                    label="Date Range"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filters.date_range"
                  range
                  @input="dateMenu = false; applyFilters()"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Messages Table -->
      <v-card elevation="4">
        <v-data-table
          :headers="headers"
          :items="messages"
          :loading="isLoading"
          :search="filters.search"
          class="messages-table"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [10, 25, 50]
          }"
        >
          <!-- Campaign Info -->
          <template v-slot:[`item.campaign_info`]="{ item }">
            <div class="campaign-info">
              <div class="campaign-name">{{ item.campaign?.campaign_name }}</div>
              <div class="contact-name">{{ item.campaign?.contact_name }}</div>
            </div>
          </template>

          <!-- Message Options -->
          <template v-slot:[`item.messages`]="{ item }">
            <div class="message-options">
              <v-chip
                v-if="item.selected_message"
                color="success"
                text-color="white"
                small
                class="mb-1"
              >
                <v-icon left small>mdi-check</v-icon>
                Selected
              </v-chip>
              <div class="options-count">{{ getMessageCount(item) }} options</div>
            </div>
          </template>

          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
              class="status-chip"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <!-- Performance -->
          <template v-slot:[`item.performance`]="{ item }">
            <div class="performance-stats">
              <div class="stat-item">
                <v-icon :color="item.was_opened ? 'success' : 'grey'" small>
                  mdi-email-open
                </v-icon>
                <span class="stat-label">{{ item.was_opened ? 'Opened' : 'Not opened' }}</span>
              </div>
              <div class="stat-item">
                <v-icon :color="item.received_reply ? 'success' : 'grey'" small>
                  mdi-reply
                </v-icon>
                <span class="stat-label">{{ item.received_reply ? 'Replied' : 'No reply' }}</span>
              </div>
              <div v-if="item.engagement_score" class="engagement-score">
                Score: {{ item.engagement_score }}
              </div>
            </div>
          </template>

          <!-- Send Time -->
          <template v-slot:[`item.send_time`]="{ item }">
            <div class="send-time">
              <div v-if="item.recommended_send_time" class="recommended-time">
                {{ formatDateTime(item.recommended_send_time) }}
              </div>
              <div v-else class="no-time">Not scheduled</div>
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="action-buttons">
              <v-btn
                icon
                small
                color="primary"
                @click="viewMessage(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <v-btn
                v-if="item.status === 'draft'"
                icon
                small
                color="success"
                @click="scheduleMessage(item)"
                :disabled="schedulingMessage === item.id"
              >
                <v-icon>mdi-calendar-clock</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="warning"
                @click="editMessage(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="deleteMessage(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Message Details Dialog -->
      <v-dialog v-model="showMessageDialog" max-width="1000px" persistent>
        <v-card v-if="selectedMessage">
          <v-card-title class="headline">
            <v-icon left color="primary">mdi-email-multiple</v-icon>
            Message Options - {{ selectedMessage.campaign?.campaign_name }}
          </v-card-title>
          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12" md="6">
                <v-card class="message-option-card" elevation="2">
                  <v-card-title class="subtitle-1">
                    <v-icon left color="blue">mdi-emoticon-happy</v-icon>
                    Option 1: Friendly
                  </v-card-title>
                  <v-card-text>
                    <div class="message-content">{{ selectedMessage.message_option_1 }}</div>
                    <v-btn
                      v-if="!selectedMessage.selected_message"
                      small
                      color="primary"
                      @click="selectMessageOption(selectedMessage, 1)"
                      class="mt-3"
                    >
                      Select This Option
                    </v-btn>
                    <v-chip
                      v-else-if="selectedMessage.selected_message === selectedMessage.message_option_1"
                      color="success"
                      text-color="white"
                      small
                      class="mt-3"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Selected
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card class="message-option-card" elevation="2">
                  <v-card-title class="subtitle-1">
                    <v-icon left color="red">mdi-clock-alert</v-icon>
                    Option 2: Urgent
                  </v-card-title>
                  <v-card-text>
                    <div class="message-content">{{ selectedMessage.message_option_2 }}</div>
                    <v-btn
                      v-if="!selectedMessage.selected_message"
                      small
                      color="primary"
                      @click="selectMessageOption(selectedMessage, 2)"
                      class="mt-3"
                    >
                      Select This Option
                    </v-btn>
                    <v-chip
                      v-else-if="selectedMessage.selected_message === selectedMessage.message_option_2"
                      color="success"
                      text-color="white"
                      small
                      class="mt-3"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Selected
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card class="message-option-card" elevation="2">
                  <v-card-title class="subtitle-1">
                    <v-icon left color="green">mdi-account-tie</v-icon>
                    Option 3: Consultative
                  </v-card-title>
                  <v-card-text>
                    <div class="message-content">{{ selectedMessage.message_option_3 }}</div>
                    <v-btn
                      v-if="!selectedMessage.selected_message"
                      small
                      color="primary"
                      @click="selectMessageOption(selectedMessage, 3)"
                      class="mt-3"
                    >
                      Select This Option
                    </v-btn>
                    <v-chip
                      v-else-if="selectedMessage.selected_message === selectedMessage.message_option_3"
                      color="success"
                      text-color="white"
                      small
                      class="mt-3"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Selected
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card class="message-option-card" elevation="2">
                  <v-card-title class="subtitle-1">
                    <v-icon left color="purple">mdi-tie</v-icon>
                    Option 4: Formal
                  </v-card-title>
                  <v-card-text>
                    <div class="message-content">{{ selectedMessage.message_option_4 }}</div>
                    <v-btn
                      v-if="!selectedMessage.selected_message"
                      small
                      color="primary"
                      @click="selectMessageOption(selectedMessage, 4)"
                      class="mt-3"
                    >
                      Select This Option
                    </v-btn>
                    <v-chip
                      v-else-if="selectedMessage.selected_message === selectedMessage.message_option_4"
                      color="success"
                      text-color="white"
                      small
                      class="mt-3"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Selected
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- AI Analysis -->
            <v-row v-if="selectedMessage.ai_analysis" class="mt-4">
              <v-col cols="12">
                <v-card class="ai-analysis-card" elevation="2">
                  <v-card-title class="subtitle-1">
                    <v-icon left color="orange">mdi-brain</v-icon>
                    AI Analysis & Recommendations
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="6">
                        <div class="analysis-item">
                          <strong>Engagement Score:</strong> {{ selectedMessage.ai_analysis.engagement_score }}/100
                        </div>
                        <div class="analysis-item">
                          <strong>Recommended Tone:</strong> {{ selectedMessage.ai_analysis.recommended_tone }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div class="analysis-item">
                          <strong>Key Insights:</strong>
                          <ul class="insights-list">
                            <li v-for="insight in selectedMessage.ai_analysis.key_insights" :key="insight">
                              {{ insight }}
                            </li>
                          </ul>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="closeMessageDialog">Close</v-btn>
            <v-btn
              v-if="selectedMessage.selected_message && selectedMessage.status === 'draft'"
              color="success"
              @click="scheduleMessage(selectedMessage)"
              :loading="schedulingMessage === selectedMessage.id"
            >
              Schedule Message
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "FollowUpBotAIMessages",
  data() {
    return {
      isLoading: false,
      messages: [],
      showMessageDialog: false,
      selectedMessage: null,
      dateMenu: false,
      schedulingMessage: null,
      selectingOption: null,
      filters: {
        search: '',
        status: '',
        tone: '',
        date_range: null
      },
      headers: [
        { text: 'Campaign', value: 'campaign_info', sortable: false },
        { text: 'Messages', value: 'messages', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Performance', value: 'performance', sortable: false },
        { text: 'Send Time', value: 'send_time', sortable: true },
        { text: 'Created', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, width: '150px' }
      ],
      statusOptions: [
        { text: 'Draft', value: 'draft' },
        { text: 'Scheduled', value: 'scheduled' },
        { text: 'Sent', value: 'sent' },
        { text: 'Failed', value: 'failed' }
      ],
      toneOptions: [
        { text: 'Friendly', value: 'friendly' },
        { text: 'Urgent', value: 'urgent' },
        { text: 'Consultative', value: 'consultative' },
        { text: 'Formal', value: 'formal' }
      ]
    };
  },
  async mounted() {
    await this.fetchMessages();
  },
  methods: {
    async fetchMessages() {
      try {
        this.isLoading = true;
        const response = await axios.get('follow-up-messages');
        this.messages = response.data.data;
      } catch (error) {
        console.error('Error fetching messages:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load messages. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDueForSending() {
      try {
        this.isLoading = true;
        const response = await axios.get('follow-up-messages/due-for-sending');
        this.messages = response.data.data;
        this.$store.dispatch('settings/showSnackbar', {
          message: `Found ${response.data.data.length} messages due for sending`,
          color: 'info'
        });
      } catch (error) {
        console.error('Error fetching due messages:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load due messages. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    async selectMessageOption(message, optionNumber) {
      try {
        this.selectingOption = `${message.id}-${optionNumber}`;
        const response = await axios.post(`follow-up-messages/${message.id}/select-option`, {
          option_number: optionNumber
        });
        
        // Update the message in our local array
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          this.messages[index] = response.data.data;
        }
        
        // Update the selected message if it's currently being viewed
        if (this.selectedMessage && this.selectedMessage.id === message.id) {
          this.selectedMessage = response.data.data;
        }
        
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Message option selected successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error selecting message option:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to select message option. Please try again.']
        });
      } finally {
        this.selectingOption = null;
      }
    },
    async scheduleMessage(message) {
      try {
        this.schedulingMessage = message.id;
        const response = await axios.post(`follow-up-messages/${message.id}/schedule`);
        
        // Update the message in our local array
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          this.messages[index] = response.data.data;
        }
        
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Message scheduled successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error scheduling message:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to schedule message. Please try again.']
        });
      } finally {
        this.schedulingMessage = null;
      }
    },
    async deleteMessage(message) {
      if (!confirm(`Are you sure you want to delete this message set?`)) {
        return;
      }
      
      try {
        await axios.delete(`follow-up-messages/${message.id}`);
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          this.messages.splice(index, 1);
        }
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Message deleted successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error deleting message:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to delete message. Please try again.']
        });
      }
    },
    viewMessage(message) {
      this.selectedMessage = message;
      this.showMessageDialog = true;
    },
    editMessage(message) {
      // Navigate to edit view
      this.$router.push(`/agents/followupbot-ai/messages/${message.id}/edit`);
    },
    closeMessageDialog() {
      this.showMessageDialog = false;
      this.selectedMessage = null;
    },
    applyFilters() {
      // Implement filtering logic if needed
    },
    getStatusColor(status) {
      const colors = {
        draft: 'grey',
        scheduled: 'orange',
        sent: 'success',
        failed: 'error'
      };
      return colors[status] || 'grey';
    },
    getStatusText(status) {
      const texts = {
        draft: 'Draft',
        scheduled: 'Scheduled',
        sent: 'Sent',
        failed: 'Failed'
      };
      return texts[status] || status;
    },
    getMessageCount(message) {
      let count = 0;
      if (message.message_option_1) count++;
      if (message.message_option_2) count++;
      if (message.message_option_3) count++;
      if (message.message_option_4) count++;
      return count;
    },
    formatDateTime(dateString) {
      if (!dateString) return 'Not set';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
  }
};
</script>

<style scoped>
.followupbot-messages {
  min-height: calc(100vh - 64px);
}

.header-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.action-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.filters-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.messages-table {
  border-radius: 16px !important;
}

.campaign-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.campaign-name {
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
}

.contact-name {
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
}

.message-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.options-count {
  font-size: 12px;
  color: #64748b;
}

.status-chip {
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.performance-stats {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
}

.engagement-score {
  font-size: 11px;
  color: #667eea;
  font-weight: 500;
}

.send-time {
  font-size: 12px;
}

.recommended-time {
  color: #2c3e50;
  font-weight: 500;
}

.no-time {
  color: #64748b;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.message-option-card {
  height: 100%;
  border-radius: 12px !important;
}

.message-content {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #2c3e50;
  white-space: pre-wrap;
  border-left: 4px solid #667eea;
}

.ai-analysis-card {
  border-radius: 12px !important;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.analysis-item {
  margin-bottom: 12px;
}

.insights-list {
  margin: 8px 0 0 20px;
  padding: 0;
}

.insights-list li {
  margin-bottom: 4px;
  color: #64748b;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .header-section {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .campaign-info {
    text-align: center;
  }
  
  .message-content {
    font-size: 12px;
    padding: 12px;
  }
}
</style>